import { useEffect, useMemo, useState } from "react";
import { PlusSmallIcon } from "@heroicons/react/20/solid";

import { usePageNameContext } from "../../contexts/PageTitleContext";
import Card from "../../components/shared/Card/Card";
import LoadingWheel from "../../components/shared/LoadingWheel";

import useHiddenPages from "../../data/useHiddenPages";
import { IHiddenPage } from "../../models/HiddenPages";
import HiddenPageModal from "../../components/hiddenPages/HiddenPageModal";
import HiddenPagesTable from "../../components/hiddenPages/HiddenPagesTable";

export default function HiddenPages() {
  const { setInfo } = usePageNameContext();
  const { hiddenPages } = useHiddenPages();

  const [modalOpen, setModalOpen] = useState(false);
  const [edit, setEdit] = useState<IHiddenPage | undefined>(undefined);

  useEffect(() => {
    setInfo({
      name: "Hidden Pages",
      desc: "Configure the pages that will not show on the widget",
    });
  }, []);

  const isLoading = hiddenPages.isLoading;
  const rows = useMemo(() => hiddenPages.data?.data ?? [], [hiddenPages.data]);

  const handleEdit = (hp: IHiddenPage) => {
    setEdit(hp);
    setModalOpen(true);
  };

  const handleAdd = () => {
    setEdit(undefined);
    setModalOpen(true);
  };

  if (isLoading) return <LoadingWheel />;

  return (
    <>
      <HiddenPageModal open={modalOpen} setOpen={setModalOpen} hiddenPage={edit} />

      <div className="space-y-4">
        <Card>
          <div className="py-4">
            <h1 className="font-semibold text-center">Hidden Pages</h1>
            <p className="text-sm text-gray-600 text-center mt-1">
              Add a page and set <span className="font-medium">IsHidden</span> to prevent it showing on the widget.
            </p>
          </div>
        </Card>

        <div className="flex justify-between">
          <button
            className="bg-primary-600 hover:bg-primary-700 inline-flex items-center rounded-md border border-transparent px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm"
            type="button"
            onClick={handleAdd}
          >
            <PlusSmallIcon className="h-5 w-5 mr-2" />
            Add Hidden Page
          </button>
        </div>

        <div className="bg-white shadow rounded p-4">
          <h1 className="text-xl font-semibold">Configured Pages</h1>
          <hr className="my-2" />

          <HiddenPagesTable hiddenPages={rows} setHiddenPage={handleEdit} />
        </div>
      </div>
    </>
  );
}
